import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import { reactive, toRefs, watch } from 'vue';
import { useSocketStore } from '@/composition/use-socket';
import { useOrdersStore } from '@/stores/orders.store';
import { createClientOrder } from '@/utils/ordering.utils';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useMenuStore } from '@/composition/use-menu';
import OrdersService from '@/api/services/orders.service';
import PaymentsService from '@/api/services/payments.service';
import { IOrder } from '@/interfaces/IOrder';
import { OrderPaymentStatus } from '@/enums/OrderEnums';
import { useUi } from '@/composition/use-ui';
import { AlertType } from '@/interfaces/IAlert';
import { Globals } from '@/modules/Globals';

// Types for queued actions
interface ICreateOrderAction {
  type: 'createOrder';
  tempId: number;
  payload: any; // Original v2Create payload
}

interface IUpdateOrderAction {
  type: 'updateOrder';
  orderId: number;
  payload: any;
}

interface ISetDiscountAction {
  type: 'setDiscount';
  orderId: number;
  discount: number;
}

interface ICreatePaymentAction {
  type: 'createPayment';
  payload: any; // PaymentsService.v2Create dto
}

export type OfflineAction =
  | ICreateOrderAction
  | IUpdateOrderAction
  | ISetDiscountAction
  | ICreatePaymentAction;

export interface IOfflineState {
  orders: Record<number, IOrder>;
  queue: OfflineAction[];
  idMap: Record<number, number>; // tempId -> serverId
  idByLocalId: Record<string, number>; // localId -> serverId
}

export const useOfflineStore = defineStore('offline', () => {
  if (!Globals.offlineOrderingEnabled) {
    // Return inert store when feature disabled
    const state = reactive({
      orders: {},
      queue: [],
      idMap: {},
      idByLocalId: {},
    });

    return {
      ...toRefs(state),
      mapId: (id: number) => id,
      offlineCreateOrder: () => {
        throw new Error('Offline ordering deshabilitado');
      },
      offlineUpdateOrder: () => null,
      offlineSetDiscount: () => null,
      offlineCreatePayment: () => null,
      processQueue: () => {},
    } as any;
  }

  // Persisted reactive state
  const persisted = useLocalStorage<IOfflineState>('offlineState', {
    orders: {},
    queue: [],
    idMap: {},
    idByLocalId: {},
  });

  // Pinia expects plain reactive object
  const state = reactive(persisted.value);

  const socketStore = useSocketStore();
  const ordersStore = useOrdersStore();
  const ui = useUi();

  /* ---------------------------------------------------------
   * Helpers
   * -------------------------------------------------------*/
  function persist() {
    // Sync back to localStorage
    persisted.value = state as IOfflineState;
  }

  function mapId(id: number): number {
    return state.idMap[id] ?? id;
  }

  function queueAction(action: OfflineAction) {
    state.queue.push(action);
    persist();
  }

  function addOfflineOrder(order: IOrder, action: ICreateOrderAction) {
    state.orders[order.id] = order;
    queueAction(action);
    ordersStore.addOrder(order, false);
    persist();
  }

  function updateLocalOrder(id: number, patch: Partial<IOrder>) {
    const order = state.orders[id];
    if (!order) return;
    Object.assign(order, patch);
    ordersStore.updateOrder(id, order);
    persist();
  }

  function replaceTempId(
    tempId: number,
    serverId: number,
    serverOrder: IOrder
  ) {
    // Map relations
    state.idMap[tempId] = serverId;
    if (serverOrder.localId) {
      state.idByLocalId[serverOrder.localId] = serverId;
    }

    // Remove the temp order from store; server order will arrive via WS
    delete state.orders[tempId];
    ordersStore.removeOrder(tempId);

    // Notify UI so open modals can switch order
    window.dispatchEvent(
      new CustomEvent('offline-order-synced', {
        detail: { tempId, serverId, localId: serverOrder.localId },
      })
    );

    persist();
  }

  /* ---------------------------------------------------------
   * Public API used by patched services / components
   * -------------------------------------------------------*/
  function offlineCreateOrder(payload: any): IOrder {
    const restaurantStore = useRestaurantStore();
    const menuStore = useMenuStore();

    // Ensure we have a complete cart object – v2Create sends cartItems separately
    const cart = payload.cart || {
      restaurantId: payload.restaurantId,
      cartItems: payload.cartItems ?? [],
      nextIndex: (payload.cartItems ?? []).length,
      userComment: payload.userComment ?? '',
    };

    const localId =
      payload.localId ?? `${Date.now()}-${Math.floor(Math.random() * 100000)}`;

    const localOrder = createClientOrder({
      restaurant: restaurantStore.restaurant!,
      branch: restaurantStore.selectedBranch!,
      table: null,
      settings: restaurantStore.restaurantSettings!,
      customFields: restaurantStore.customFields,
      orderType: payload.orderType ?? menuStore.selectedOrderType!,
      cart,
      deliveryInfo: payload.deliveryInfo,
      paymentMethod: payload.paymentMethod,
      customerData: payload.customerData,
      customFieldValues: payload.customFieldValues,
      deliveryZones: restaurantStore.deliveryZones,
      deviceId: payload.deviceId ?? restaurantStore.deviceId,
      localId,
    });

    // Ensure localId also present in payload we queue
    payload.localId = localId;

    addOfflineOrder(localOrder, {
      type: 'createOrder',
      tempId: localOrder.id,
      payload,
    });

    return localOrder;
  }

  function offlineUpdateOrder(orderId: number, patch: any): IOrder | null {
    if (!state.orders[orderId]) return null;
    updateLocalOrder(orderId, patch);
    queueAction({
      type: 'updateOrder',
      orderId,
      payload: patch,
    });
    return state.orders[orderId];
  }

  function offlineSetDiscount(
    orderId: number,
    discount: number
  ): IOrder | null {
    if (!state.orders[orderId]) return null;
    updateLocalOrder(orderId, { discount } as any);
    queueAction({
      type: 'setDiscount',
      orderId,
      discount,
    });
    return state.orders[orderId];
  }

  function offlineCreatePayment(dto: any) {
    // Minimal IPayment-like object
    const payment = {
      id: Math.floor(Math.random() * 1_000_000),
      total: dto.total,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as any;

    // Update orders locally
    for (const id of dto.entityIds) {
      const order = state.orders[id];
      if (!order) continue;
      order.totalPaid = (order.totalPaid ?? 0) + dto.total;
      if (order.totalPaid >= order.total) {
        order.paymentStatus = OrderPaymentStatus.Paid;
      }
      ordersStore.updateOrder(id, order);
    }

    queueAction({
      type: 'createPayment',
      payload: dto,
    });

    return payment;
  }

  /* ---------------------------------------------------------
   * Queue processing when back online
   * -------------------------------------------------------*/
  async function processQueue() {
    if (!socketStore.isConnected) return;

    console.log('Processing queue', state.queue);

    for (let i = 0; i < state.queue.length; ) {
      const action = state.queue[i];
      try {
        switch (action.type) {
          case 'createOrder': {
            const serverOrder = await OrdersService.v2Create(action.payload);
            replaceTempId(action.tempId, serverOrder.id, serverOrder);
            break;
          }
          case 'updateOrder': {
            const serverId = mapId(action.orderId);
            await OrdersService.v2Update(serverId, action.payload);
            break;
          }
          case 'setDiscount': {
            const serverId = mapId(action.orderId);
            await OrdersService.v2Update(serverId, {
              legacyDiscount: action.discount,
            });
            break;
          }
          case 'createPayment': {
            const dto = { ...action.payload };
            dto.entityIds = dto.entityIds.map((id: number) => mapId(id));
            await PaymentsService.v2Create(dto);
            break;
          }
        }
        // Success → remove processed action, do NOT increment index
        state.queue.splice(i, 1);
        continue; // continue loop with same index value
      } catch (err) {
        console.error('[Offline] Failed processing action', action, err);

        ui.showToast(
          AlertType.Error,
          `Error al sincronizar acción offline (${action.type}): ${
            err?.message || err
          }`
        );

        // Remove failed action as well to avoid infinite loop
        state.queue.splice(i, 1);

        // If it was a createOrder and it never synced, also drop the local order copy
        if (action.type === 'createOrder') {
          delete state.orders[action.tempId];
          ordersStore.removeOrder(action.tempId);
        }
        continue;
      }
    }
    persist();
  }

  // Auto process when connection restored
  watch(
    () => socketStore.isConnected,
    (connected) => {
      if (connected) {
        processQueue();
      }
    },
    { immediate: true }
  );

  /* ---------------------------------------------------------*/
  return {
    ...toRefs(state),
    mapId,
    offlineCreateOrder,
    offlineUpdateOrder,
    offlineSetDiscount,
    offlineCreatePayment,
    processQueue,
  };
});
